import React, { useState } from 'react';
import { useQuery, useMutation } from 'react-query';
import { motion } from 'framer-motion';
import { FiSend, FiCheckCircle } from 'react-icons/fi';
import { adminApi } from '../../utils/api';
import './AdminMessagesPage.css';

const AdminMessagesPage = () => {
  const [selectedCreatorId, setSelectedCreatorId] = useState('');
  const [selectedCampaignId, setSelectedCampaignId] = useState('');
  const [messageData, setMessageData] = useState({ campaignName: '', content: '' });
  const [messageSent, setMessageSent] = useState(false);

  // Load campaigns for the dropdown
  const { data: campaignsData, isLoading: loadingCampaigns } = useQuery(
    'admin-all-campaigns',
    () => adminApi.getCampaigns({ limit: 1000 }),
    { staleTime: 60000 }
  );

  // Load creators filtered by selected campaign
  const { data: creatorsData, isLoading: loadingCreators } = useQuery(
    ['admin-creators-by-campaign', selectedCampaignId],
    () => adminApi.getCreators({ limit: 1000, campaignId: selectedCampaignId || undefined }),
    {
      staleTime: 60000,
      enabled: !!selectedCampaignId // Only fetch when a campaign is selected
    }
  );

  const sendMessageMutation = useMutation(
    (data) => adminApi.sendMessage(data),
    {
      onSuccess: () => {
        setMessageSent(true);
        setMessageData({ campaignName: '', content: '' });
        setSelectedCreatorId('');
        setSelectedCampaignId('');
        setTimeout(() => {
          setMessageSent(false);
        }, 3000);
      }
    }
  );

  const creators = creatorsData?.data?.creators || [];
  const campaigns = campaignsData?.data?.campaigns || [];

  const handleSend = () => {
    if (!selectedCreatorId || !messageData.content) return;
    sendMessageMutation.mutate({
      receiverId: selectedCreatorId,
      subject: messageData.campaignName,
      content: messageData.content
    });
  };

  return (
    <div className="admin-page admin-messages-page">
      <header className="admin-header">
        <div className="header-content">
          <h1>שליחת הודעה</h1>
        </div>
      </header>

      <div className="message-composer">
        {messageSent ? (
          <motion.div
            className="success-message"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <FiCheckCircle className="success-icon" />
            <h2>ההודעה נשלחה בהצלחה!</h2>
          </motion.div>
        ) : (
          <div className="message-form-container">
            {/* Campaign Select Dropdown */}
            <div className="form-group">
              <label>קמפיין</label>
              {loadingCampaigns ? (
                <div className="loading-select">טוען קמפיינים...</div>
              ) : (
                <select
                  value={selectedCampaignId}
                  onChange={(e) => {
                    const campaignId = e.target.value;
                    const campaign = campaigns.find(c => c.id === campaignId);
                    setSelectedCampaignId(campaignId);
                    setMessageData({ ...messageData, campaignName: campaign?.title || '' });
                    setSelectedCreatorId(''); // Reset creator selection when campaign changes
                  }}
                  className="form-select"
                >
                  <option value="">בחר קמפיין...</option>
                  {campaigns.map((campaign) => (
                    <option key={campaign.id} value={campaign.id}>
                      {campaign.title}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Message Content */}
            <div className="form-group">
              <label>הודעה *</label>
              <textarea
                value={messageData.content}
                onChange={(e) => setMessageData({ ...messageData, content: e.target.value })}
                placeholder="כתוב את ההודעה..."
                rows={8}
                className="form-textarea"
              />
            </div>

            {/* Creator SELECT Dropdown */}
            <div className="form-group">
              <label>שלח אל *</label>
              {!selectedCampaignId ? (
                <div className="loading-select">בחר קמפיין תחילה...</div>
              ) : loadingCreators ? (
                <div className="loading-select">טוען משפיענים...</div>
              ) : (
                <select
                  value={selectedCreatorId}
                  onChange={(e) => setSelectedCreatorId(e.target.value)}
                  className="form-select"
                >
                  <option value="">בחר משפיען...</option>
                  {creators.map((creator) => (
                    <option key={creator.id} value={creator.id}>
                      {creator.display_name || creator.full_name} - {creator.email}
                    </option>
                  ))}
                </select>
              )}
              {selectedCampaignId && creators.length === 0 && !loadingCreators && (
                <p className="no-creators-message">אין משפיענים שהגישו לקמפיין זה</p>
              )}
            </div>

            {/* Send Button */}
            <button
              className="btn btn-primary btn-send"
              onClick={handleSend}
              disabled={!messageData.content || !selectedCreatorId || sendMessageMutation.isLoading}
            >
              <FiSend />
              {sendMessageMutation.isLoading ? 'שולח...' : 'שלח הודעה'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminMessagesPage;
